import { icons } from '../assets/icons';
import { INSTALL_SNIPPETS, ReleaseInfo, FALLBACK_RELEASE } from '../config/project-info';

type InstallTabKey = 'pipx' | 'deb' | 'arch' | 'rpm' | 'appimage' | 'windows' | 'source' | 'termux';

export class InstallTabsComponent {
  private activeTab: InstallTabKey = 'pipx';
  private release: ReleaseInfo = FALLBACK_RELEASE;

  constructor(release: ReleaseInfo = FALLBACK_RELEASE) {
    this.release = release;
  }

  public updateRelease(release: ReleaseInfo) {
    this.release = release;
    const codeEl = document.getElementById('install-code-content');
    if (codeEl) {
      codeEl.textContent = this.getSnippet(this.activeTab);
    }
  }

  public render(): string {
    const tabs: { key: InstallTabKey; label: string; icon: string }[] = [
      { key: 'pipx', label: 'pipx (Python)', icon: icons.terminal('w-4 h-4') },
      { key: 'deb', label: 'Debian / Ubuntu', icon: icons.server('w-4 h-4') },
      { key: 'arch', label: 'Arch Linux', icon: icons.packageIcon('w-4 h-4') },
      { key: 'rpm', label: 'Fedora / RPM', icon: icons.server('w-4 h-4') },
      { key: 'appimage', label: 'AppImage', icon: icons.packageIcon('w-4 h-4') },
      { key: 'windows', label: 'Windows', icon: icons.terminal('w-4 h-4') },
      { key: 'source', label: 'Poetry (Source)', icon: icons.code('w-4 h-4') },
      { key: 'termux', label: 'Android (Termux)', icon: icons.layers('w-4 h-4') },
    ];

    return `
      <section id="install" class="py-16 md:py-24 border-t border-terminal-border bg-terminal-bgDark/40">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div class="text-center max-w-3xl mx-auto space-y-3 mb-12">
            <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-semibold bg-matrix-950/80 text-matrix-400 border border-matrix-800 shadow-glow-sm">
              <span>// INSTALLATION HUB</span>
            </div>
            <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              Get Started with Mastui
            </h2>
            <p class="text-sm sm:text-base text-terminal-muted font-mono">
              Choose your platform or package format. Pre-built native binaries require zero Python setup.
            </p>
          </div>

          <div class="max-w-4xl mx-auto space-y-4">
            
            <!-- Tabs Bar -->
            <div class="flex flex-wrap gap-1.5 p-1.5 rounded-2xl bg-terminal-card border border-terminal-border">
              ${tabs.map(tab => `
                <button 
                  data-install-tab="${tab.key}"
                  class="install-tab-btn flex-1 min-w-[130px] flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-mono font-semibold transition-all cursor-pointer ${
                    this.activeTab === tab.key 
                      ? 'bg-matrix-500 text-terminal-bgDark shadow-glow' 
                      : 'text-terminal-muted hover:text-terminal-fg hover:bg-terminal-cardLight'
                  }"
                >
                  ${tab.icon}
                  <span>${tab.label}</span>
                </button>
              `).join('')}
            </div>

            <!-- Terminal Snippet Box -->
            <div class="relative rounded-2xl bg-terminal-bgDark border border-terminal-border shadow-glow-lg overflow-hidden font-mono">
              <div class="flex items-center justify-between px-4 py-3 bg-terminal-card/80 border-b border-terminal-border">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded-full bg-rose-500/80"></div>
                  <div class="w-3 h-3 rounded-full bg-amber-500/80"></div>
                  <div class="w-3 h-3 rounded-full bg-matrix-500/80"></div>
                  <span id="install-snippet-title" class="ml-2 text-xs text-matrix-400">bash :: install-mastui</span>
                </div>
                
                <button 
                  id="install-copy-btn"
                  class="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold text-matrix-400 hover:text-matrix-300 bg-terminal-bgDark border border-terminal-border hover:border-matrix-500 transition-all cursor-pointer"
                >
                  <span id="install-copy-icon">${icons.copy('w-3.5 h-3.5')}</span>
                  <span id="install-copied-icon" class="hidden text-matrix-300">${icons.check('w-3.5 h-3.5')}</span>
                  <span id="install-copy-text">Copy</span>
                </button>
              </div>

              <div class="p-6 overflow-x-auto">
                <pre class="text-xs sm:text-sm leading-relaxed text-matrix-300"><code id="install-code-content">${this.getSnippet(this.activeTab)}</code></pre>
              </div>
            </div>

            <!-- Extra Info Footnote -->
            <div class="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs font-mono text-terminal-muted px-2">
              <div class="flex items-center gap-1.5">
                ${icons.zap('w-4 h-4 text-cyber-400')}
                <span>Supports Python 3.9 - 3.14 on Linux, macOS, Windows & Termux</span>
              </div>
              
              <a href="#/docs/Installation" class="text-matrix-400 hover:text-matrix-300 hover:underline font-semibold flex items-center gap-1">
                <span>Detailed Installation Docs</span>
                ${icons.arrowRight('w-3.5 h-3.5')}
              </a>
            </div>

          </div>

        </div>
      </section>
    `;
  }

  private getSnippet(tab: InstallTabKey): string {
    const rawVersion = this.release.version || 'v1.12.0';
    const cleanVersion = rawVersion.replace(/^v/i, '');

    const debAsset = this.release.assets?.find(a => a.type === 'deb');
    const debUrl = debAsset ? debAsset.downloadUrl : `https://github.com/kimusan/mastui/releases/download/${rawVersion}/mastui_${cleanVersion}_amd64.deb`;

    const rpmAsset = this.release.assets?.find(a => a.type === 'rpm');
    const rpmUrl = rpmAsset ? rpmAsset.downloadUrl : `https://github.com/kimusan/mastui/releases/download/${rawVersion}/mastui-${cleanVersion}-1.x86_64.rpm`;

    const archAsset = this.release.assets?.find(a => a.type === 'arch');
    const archUrl = archAsset ? archAsset.downloadUrl : `https://github.com/kimusan/mastui/releases/download/${rawVersion}/mastui-${cleanVersion}-1-x86_64.pkg.tar.zst`;

    const appImageAsset = this.release.assets?.find(a => a.type === 'appimage');
    const appImageUrl = appImageAsset ? appImageAsset.downloadUrl : `https://github.com/kimusan/mastui/releases/download/${rawVersion}/mastui-${cleanVersion}-x86_64.AppImage`;

    switch (tab) {
      case 'pipx':
        return `# 1) Install pipx (if needed)
python3 -m pip install --user pipx
python3 -m pipx ensurepath

# 2) Install Mastui
pipx install mastui

# 3) Launch Mastui
mastui`;

      case 'deb':
        return `# Download and install .deb on Debian/Ubuntu (${rawVersion})
wget ${debUrl}
sudo apt install ./${debUrl.split('/').pop()}

# Launch
mastui`;

      case 'arch':
        return `# Download and install via pacman on Arch Linux (${rawVersion})
wget ${archUrl}
sudo pacman -U ${archUrl.split('/').pop()}

# Launch
mastui`;

      case 'rpm':
        return `# Download and install .rpm on Fedora / RHEL (${rawVersion})
wget ${rpmUrl}
sudo dnf install ./${rpmUrl.split('/').pop()}

# Launch
mastui`;

      case 'appimage':
        return `# Download and run standalone AppImage (${rawVersion})
wget ${appImageUrl}
chmod +x ${appImageUrl.split('/').pop()}
./${appImageUrl.split('/').pop()}`;

      case 'windows':
        return `# In Windows Terminal / PowerShell:
# Download mastui-${cleanVersion}-windows-x86_64.zip from GitHub Releases:
Invoke-WebRequest -Uri "https://github.com/kimusan/mastui/releases/download/${rawVersion}/mastui-${cleanVersion}-windows-x86_64.zip" -OutFile "mastui.zip"
Expand-Archive mastui.zip -DestinationPath .
.\\mastui.exe`;

      case 'source':
        return `# Clone and build with Poetry
git clone https://github.com/kimusan/mastui.git
cd mastui
poetry install
poetry run mastui`;

      case 'termux':
        return `# On Android using Termux:
pkg update && pkg upgrade
pkg install python python-pip
pip install mastui
mastui`;

      default:
        return INSTALL_SNIPPETS[tab] || INSTALL_SNIPPETS.pipx;
    }
  }

  public bindEvents() {
    const tabButtons = document.querySelectorAll('.install-tab-btn');
    const codeEl = document.getElementById('install-code-content');
    const copyBtn = document.getElementById('install-copy-btn');
    const copyIcon = document.getElementById('install-copy-icon');
    const copiedIcon = document.getElementById('install-copied-icon');
    const copyText = document.getElementById('install-copy-text');

    tabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const targetTab = btn.getAttribute('data-install-tab') as InstallTabKey;
        if (!targetTab) return;

        this.activeTab = targetTab;

        tabButtons.forEach(b => {
          b.className = 'install-tab-btn flex-1 min-w-[130px] flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-mono font-semibold transition-all cursor-pointer text-terminal-muted hover:text-terminal-fg hover:bg-terminal-cardLight';
        });

        btn.className = 'install-tab-btn flex-1 min-w-[130px] flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-mono font-semibold transition-all cursor-pointer bg-matrix-500 text-terminal-bgDark shadow-glow';

        if (codeEl) {
          codeEl.textContent = this.getSnippet(targetTab);
        }
      });
    });

    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        const currentCode = this.getSnippet(this.activeTab);
        try {
          await navigator.clipboard.writeText(currentCode);
          if (copyIcon && copiedIcon && copyText) {
            copyIcon.classList.add('hidden');
            copiedIcon.classList.remove('hidden');
            copyText.textContent = 'Copied!';
            setTimeout(() => {
              copyIcon.classList.remove('hidden');
              copiedIcon.classList.add('hidden');
              copyText.textContent = 'Copy';
            }, 2000);
          }
        } catch {
          // ignore
        }
      });
    }
  }
}
